import React, { useCallback, useEffect, useRef, useState } from 'react';

interface ReadingNudgePillProps {
  onDismiss?: () => void;
  onClick?: () => void;
  autoHideMs?: number;
  label?: string;
}

const ReadingNudgePill: React.FC<ReadingNudgePillProps> = ({
  onDismiss,
  onClick,
  autoHideMs = 10000,
  label = 'Read source passages',
}) => {
  const [visible, setVisible] = useState(true);
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    if (exiting) return;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setExiting(true);
    exitTimerRef.current = setTimeout(() => {
      setVisible(false);
      onDismiss?.();
    }, 250);
  }, [exiting, onDismiss]);

  useEffect(() => {
    if (autoHideMs > 0) {
      timerRef.current = setTimeout(() => {
        dismiss();
      }, autoHideMs);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
  }, [autoHideMs, dismiss]);

  if (!visible) return null;

  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        opacity: exiting ? 0 : 1,
        transform: exiting ? 'translateY(-6px) scale(0.96)' : 'translateY(0) scale(1)',
        transition: 'opacity 0.25s ease, transform 0.25s ease',
      }}
    >
      <div
        onClick={() => {
          onClick?.();
          dismiss();
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          borderRadius: 24,
          padding: '8px 12px 8px 8px',
          fontSize: 14,
          fontWeight: 500,
          fontFamily: "'DM Sans', system-ui, sans-serif",
          color: '#fff',
          background: 'linear-gradient(102deg, #1d4ed8 0%, #3b82f6 48%, #60a5fa 100%)',
          boxShadow: '0 2px 8px rgba(37,99,235,0.25)',
          cursor: 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.05em',
            padding: '3px 8px',
            borderRadius: 6,
            background: 'rgba(255,255,255,0.22)',
            color: '#fff',
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          NEW
        </span>

        <svg
          width={17}
          height={17}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          style={{ flexShrink: 0, opacity: 0.9 }}
          aria-hidden="true"
        >
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
        </svg>

        <span>{label}</span>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            dismiss();
          }}
          style={{
            border: 'none',
            background: 'transparent',
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontSize: 15,
            fontWeight: 500,
            fontFamily: "'DM Sans', system-ui, sans-serif",
            lineHeight: 1,
            padding: '2px 0 2px 4px',
            opacity: 0.7,
            transition: 'opacity 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0.7';
          }}
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>

      <div
        style={{
          width: 12,
          height: 6,
          clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
          background: '#4d8af0',
          flexShrink: 0,
        }}
      />
    </div>
  );
};

export default ReadingNudgePill;
